<template>
  <s-app-layout
    :title="translate('App.Title')"
    :src-image="appLogo"
    :user-profile="userData"
    :show-notifications="false"
    :language="appLanguages"
    :with-navigation-sidebar="false"
    @click:logout="logout"
    @click:current-language="onLanguageSelected"
  >
    <div class="ssc-flex-grow ssc-overflow-y-auto relative h-full">
      <router-view />
    </div>
    <template v-if="userIsMigrated" #profile-content>
      <app-user-profile-options />
    </template>
    <template #content-head-right>
      <company-selection
        :companies="companies"
        :selected-company="selectedCompany"
        @company-selected="onCompanySelected"
      />
      <v-divider vertical opacity="100" thickness="2px" class="rounded" />
      <s-icon-button
        role="button"
        :icon="'mdi-help'"
        aria-label="Help"
        :title="translate('App.Help')"
        data-testid="help"
        @icon-button-click="onClickHelp"
      />
      <contextual-app-menu
        v-if="appStore.showContextualMenu"
        :principal-apps="appStore.appsByType.principal"
        :secondary-apps="appStore.appsByType.secondary"
        @open="onContextualMenuClick"
      />
    </template>
    <template #toolbar-right-content>
      <v-btn
        class="!normal-case"
        variant="flat"
        rounded="lg"
        color="#1573E5"
        @click="onClickNewsletter"
      >
        {{ translate('App.Newsletter') }}
      </v-btn>
    </template>
  </s-app-layout>
</template>
<script setup lang="ts">
import { useSettingsStore } from '@/store'
import { onBeforeMount, computed, watch, inject } from 'vue'
import { SAppLayout, SIconButton } from 'sima-suite-components-vue3'
import { Language } from './types'
import appLogo from '@/assets/applogo.svg'
import AppUserProfileOptions from '@/components/AppUserProfileOptions/AppUserProfileOptions.vue'
import ContextualAppMenu from '@/components/ContextualAppMenu/ContextualAppMenu.vue'
import { auth } from '@/plugins'
import { event } from '@/plugins/analytics'
import { LanguageKey, useTranslator } from '@/plugins/translations'
import { productFruits } from '@/plugins'
import { validate as uuidValidate } from 'uuid'
import CompanySelection from '@/plugins/companySelection/CompanySelection.vue'
import { companyDictionaryKey, Company } from '@/plugins/companySelection/types'
import { useCompanies } from '@/plugins/companySelection/companies'

const { logout, getUserProfile } = auth.useAuthentication()

const appStore = useSettingsStore()
const { translate, locale, setLanguage } = useTranslator()
const userIsMigrated = computed(() => (userData.value ? uuidValidate(userData.value.sub) : false))

const onClickHelp = () => {
  window.open(appStore.urls.helpUrl, '_blank')?.focus()
}

const onContextualMenuClick = () => {
  event('menu_contextual.opened.workspace')
}

const onClickNewsletter = () => {
  window.open(appStore.urls.newsletterUrl, '_blank')?.focus()
}

const languages = [{ key: LanguageKey.es }, { key: LanguageKey.en }]

const userData = computed(() => {
  return getUserProfile()
})

const appLanguages = computed(() => {
  const langs: { key: string; value: any }[] = []
  const currentLanguage = languages.find((lang: Language) => lang.key === locale.value)
  langs.push(createAppLanguage(currentLanguage!.key))
  languages.forEach((lang: Language) => {
    if (lang.key !== locale.value) {
      langs.push(createAppLanguage(lang.key))
    }
  })

  return {
    options: langs,
    visibility: true,
    default: langs[0]
  }
})

const createAppLanguage = (lang: string) => {
  return {
    key: lang,
    value: translate(`App.Language.${lang}`)
  }
}

const onLanguageSelected = (selectedLanguage: Language) => {
  setLanguage(selectedLanguage.key as LanguageKey)
}

const { companies, selectedCompany, selectedCompanyId, setCompanies } = useCompanies()

const onCompanySelected = async (company: Company) => {
  selectedCompanyId.value = company.id
}

watch(
  companies,
  (newCompanies) => {
    if (!selectedCompany.value && newCompanies.length > 0) {
      selectedCompanyId.value = newCompanies[0].id
    }
  },
  { immediate: true }
)

onBeforeMount(async () => {
  const user = getUserProfile()
  productFruits.init({
    key: appStore.urls.productFruitKey,
    language: window.navigator.language.split('-')[0],
    user
  })
  const companyDictionary = inject(companyDictionaryKey, {})
  setCompanies(companyDictionary)
})
</script>

<style scoped>
:deep(.ssc-flex) {
  align-items: center;
}
</style>
